import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Campaign as CampaignIcon,
  Build as BuildIcon,
  Info as InfoIcon,
  NewReleases as NewReleasesIcon,
  CheckCircle as CheckCircleIcon,
  VisibilityOff as VisibilityOffIcon,
  DoneAll as DoneAllIcon,
  ArrowBack as ArrowBackIcon,
  PushPin as PushPinIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { mitteilungenApi } from '../services/api';
import type { Mitteilung, MitteilungenResponse, ReleaseVersion } from '../types/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const getPriorityColor = (prioritaet: string): 'default' | 'primary' | 'warning' | 'error' => {
  switch (prioritaet) {
    case 'kritisch': return 'error';
    case 'hoch': return 'warning';
    case 'normal': return 'primary';
    default: return 'default';
  }
};

const getTypeIcon = (typ: string) => {
  switch (typ) {
    case 'ankuendigung': return <CampaignIcon />;
    case 'wartung': return <BuildIcon />;
    case 'release_note': return <NewReleasesIcon />;
    default: return <InfoIcon />;
  }
};

const getTypLabel = (typ: string): string => {
  switch (typ) {
    case 'ankuendigung': return 'Ankündigung';
    case 'wartung': return 'Wartung';
    case 'release_note': return 'Release';
    case 'info': return 'Info';
    default: return typ;
  }
};

const getCategoryColor = (kategorie: string): string => {
  switch (kategorie) {
    case 'neu': return '#4caf50';
    case 'verbessert': return '#2196f3';
    case 'bugfix': return '#ff9800';
    case 'sicherheit': return '#f44336';
    case 'entfernt': return '#9e9e9e';
    default: return '#757575';
  }
};

const getCategoryLabel = (kategorie: string): string => {
  switch (kategorie) {
    case 'neu': return 'Neu';
    case 'verbessert': return 'Verbessert';
    case 'bugfix': return 'Bugfix';
    case 'sicherheit': return 'Sicherheit';
    case 'entfernt': return 'Entfernt';
    default: return kategorie;
  }
};

const Mitteilungen: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [tabValue, setTabValue] = useState(0);
  const [mitteilungen, setMitteilungen] = useState<Mitteilung[]>([]);
  const [releaseVersions, setReleaseVersions] = useState<ReleaseVersion[]>([]);
  const [ungeleseneCount, setUngeleseneCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMitteilung, setSelectedMitteilung] = useState<Mitteilung | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  useEffect(() => {
    if (id) {
      loadMitteilungDetail(parseInt(id));
    }
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (tabValue === 0) {
        const response = await mitteilungenApi.getAll({ page: 1, pageSize: 50 });
        const data = response.data as MitteilungenResponse;
        setMitteilungen(data.mitteilungen || []);
        setUngeleseneCount(data.ungeleseneCount || 0);
      } else {
        const response = await mitteilungenApi.getReleaseNotes();
        setReleaseVersions(response.data || []);
      }
    } catch (err: any) {
      console.error('Fehler beim Laden:', err);
      setError('Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const loadMitteilungDetail = async (mitteilungId: number) => {
    try {
      const response = await mitteilungenApi.getById(mitteilungId);
      setSelectedMitteilung(response.data);
      setDetailDialogOpen(true);

      // Auto-mark as read
      if (!response.data.istGelesen) {
        await mitteilungenApi.alsGelesenMarkieren(mitteilungId);
        setMitteilungen(prev =>
          prev.map(m => m.mitteilungID === mitteilungId ? { ...m, istGelesen: true } : m)
        );
        setUngeleseneCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Fehler beim Laden der Mitteilung:', err);
    }
  };

  const handleMarkAsRead = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await mitteilungenApi.alsGelesenMarkieren(id);
      setMitteilungen(prev =>
        prev.map(m => m.mitteilungID === id ? { ...m, istGelesen: true } : m)
      );
      setUngeleseneCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Fehler:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await mitteilungenApi.alleAlsGelesenMarkieren();
      setMitteilungen(prev => prev.map(m => ({ ...m, istGelesen: true })));
      setUngeleseneCount(0);
    } catch (err) {
      console.error('Fehler:', err);
    }
  };

  const handleHide = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await mitteilungenApi.verstecken(id);
      setMitteilungen(prev => prev.filter(m => m.mitteilungID !== id));
    } catch (err) {
      console.error('Fehler:', err);
    }
  };

  const handleMitteilungClick = (mitteilung: Mitteilung) => {
    navigate(`/mitteilungen/${mitteilung.mitteilungID}`);
  };

  const handleCloseDetail = () => {
    setDetailDialogOpen(false);
    setSelectedMitteilung(null);
    navigate('/mitteilungen');
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={() => navigate('/')}>
              <ArrowBackIcon />
            </IconButton>
            <Badge badgeContent={ungeleseneCount} color="error">
              <NotificationsIcon color="primary" fontSize="large" />
            </Badge>
            <Typography variant="h5">Mitteilungen & Release-Notes</Typography>
          </Box>
          {tabValue === 0 && ungeleseneCount > 0 && (
            <Button
              variant="outlined"
              startIcon={<DoneAllIcon />}
              onClick={handleMarkAllAsRead}
            >
              Alle als gelesen markieren
            </Button>
          )}
        </Box>

        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <CampaignIcon fontSize="small" />
                Mitteilungen
                {ungeleseneCount > 0 && (
                  <Chip label={ungeleseneCount} size="small" color="error" />
                )}
              </Box>
            }
          />
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <NewReleasesIcon fontSize="small" />
                Release-Notes
              </Box>
            }
          />
        </Tabs>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <TabPanel value={tabValue} index={0}>
            {mitteilungen.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  Keine Mitteilungen vorhanden
                </Typography>
              </Paper>
            ) : (
              <Paper>
                <List disablePadding>
                  {mitteilungen.map((mitteilung, index) => (
                    <React.Fragment key={mitteilung.mitteilungID}>
                      <ListItem
                        sx={{
                          py: 2,
                          px: 3,
                          bgcolor: mitteilung.istGelesen ? 'transparent' : 'action.hover',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.selected' },
                        }}
                        onClick={() => handleMitteilungClick(mitteilung)}
                      >
                        <ListItemIcon>
                          {getTypeIcon(mitteilung.typ)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              {mitteilung.istAngepinnt && (
                                <PushPinIcon fontSize="small" color="warning" />
                              )}
                              <Typography
                                variant="subtitle1"
                                fontWeight={mitteilung.istGelesen ? 'normal' : 'bold'}
                              >
                                {mitteilung.titel}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box mt={1}>
                              <Typography
                                variant="body2"
                                color="textSecondary"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: mitteilung.inhalt.substring(0, 200) + (mitteilung.inhalt.length > 200 ? '...' : '')
                                }}
                              />
                              <Box display="flex" alignItems="center" gap={1} mt={1}>
                                <Chip
                                  label={getTypLabel(mitteilung.typ)}
                                  size="small"
                                  variant="outlined"
                                />
                                <Chip
                                  label={mitteilung.prioritaet}
                                  size="small"
                                  color={getPriorityColor(mitteilung.prioritaet)}
                                />
                                <Typography variant="caption" color="textSecondary">
                                  {new Date(mitteilung.erstelltAm).toLocaleDateString('de-DE', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  von {mitteilung.erstelltVonName}
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                        <Box display="flex" gap={1}>
                          {!mitteilung.istGelesen && (
                            <Tooltip title="Als gelesen markieren">
                              <IconButton
                                size="small"
                                onClick={(e) => handleMarkAsRead(mitteilung.mitteilungID, e)}
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Ausblenden">
                            <IconButton
                              size="small"
                              onClick={(e) => handleHide(mitteilung.mitteilungID, e)}
                            >
                              <VisibilityOffIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListItem>
                      {index < mitteilungen.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {releaseVersions.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  Keine Release-Notes vorhanden
                </Typography>
              </Paper>
            ) : (
              releaseVersions.map((version) => (
                <Paper key={version.version} sx={{ p: 3, mb: 3 }}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <NewReleasesIcon color="primary" />
                    <Typography variant="h6">Version {version.version}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {new Date(version.veroeffentlichtAm).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </Typography>
                  </Box>
                  <List disablePadding>
                    {version.eintraege.map((note, index) => (
                      <ListItem key={note.releaseNoteID} sx={{ py: 1 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: getCategoryColor(note.kategorie),
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip
                                label={getCategoryLabel(note.kategorie)}
                                size="small"
                                sx={{
                                  bgcolor: getCategoryColor(note.kategorie),
                                  color: 'white',
                                  fontWeight: 'bold',
                                }}
                              />
                              <Typography variant="subtitle2">
                                {note.titel}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography
                              component="span"
                              variant="body2"
                              color="textSecondary"
                              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                            >
                              {note.beschreibung}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              ))
            )}
          </TabPanel>
        </>
      )}

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
      >
        {selectedMitteilung && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" gap={2}>
                {getTypeIcon(selectedMitteilung.typ)}
                {selectedMitteilung.titel}
                {selectedMitteilung.istAngepinnt && (
                  <Chip label="Wichtig" size="small" color="warning" />
                )}
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Box mb={2} display="flex" gap={1} flexWrap="wrap">
                <Chip
                  label={getTypLabel(selectedMitteilung.typ)}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={selectedMitteilung.prioritaet}
                  size="small"
                  color={getPriorityColor(selectedMitteilung.prioritaet)}
                />
                <Typography variant="body2" color="textSecondary">
                  Erstellt am {new Date(selectedMitteilung.erstelltAm).toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })} von {selectedMitteilung.erstelltVonName}
                </Typography>
              </Box>
              <Box
                sx={{
                  '& p': { my: 1 },
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
                dangerouslySetInnerHTML={{ __html: selectedMitteilung.inhalt }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetail}>Schließen</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Mitteilungen;
